import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  Users, Activity, CreditCard, LifeBuoy, TrendingUp, AlertTriangle,
  Zap, DollarSign, XCircle, Search, Download, Bot, Server, ShieldAlert,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area,
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { ZITA, CHART_TOKENS } from '@/constants/brandTokens';

const RANGES = [
  { v: '7d', l: 'Last 7 days' },
  { v: '30d', l: 'Last 30 days' },
  { v: '90d', l: 'Last 90 days' },
  { v: 'all', l: 'All time' },
];

const KpiCard = ({ icon: Icon, label, value, sub, tid, accent }) => (
  <div className="zita-card p-5" data-testid={tid}>
    <div className="flex items-center gap-3 mb-2">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${accent || 'bg-zita-sand text-zita-cocoa'}`}>
        <Icon size={14} />
      </div>
      <div className="text-xs uppercase tracking-wider text-zita-mocha">{label}</div>
    </div>
    <div className="text-3xl font-serif text-zita-espresso" data-testid={`${tid}-value`}>{value}</div>
    {sub && <div className="text-xs text-zita-mocha mt-1">{sub}</div>}
  </div>
);

const TrendCard = ({ title, series, kind = 'area', tid, stroke = ZITA.blue, valueFmt }) => {
  const data = series || [];
  const fmt = valueFmt || ((v) => v);
  return (
    <div className="zita-card p-5" data-testid={tid}>
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-xs uppercase tracking-wider text-zita-mocha">{title}</div>
        <div className="text-xs text-zita-cocoa">
          {fmt(data.reduce((a, b) => a + (b.value || 0), 0))} total
        </div>
      </div>
      <div style={{ width: '100%', height: 140, minHeight: 140 }}>
        <ResponsiveContainer minHeight={140} width="100%" height="100%">
          {kind === 'bar' ? (
            <BarChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_TOKENS.grid} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: CHART_TOKENS.axisText }} />
              <YAxis tick={{ fontSize: 10, fill: CHART_TOKENS.axisText }} allowDecimals={false} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Bar dataKey="value" fill={stroke} radius={[4, 4, 0, 0]} />
            </BarChart>
          ) : kind === 'line' ? (
            <LineChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_TOKENS.grid} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: CHART_TOKENS.axisText }} />
              <YAxis tick={{ fontSize: 10, fill: CHART_TOKENS.axisText }} allowDecimals={false} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Line type="monotone" dataKey="value" stroke={stroke} strokeWidth={2} dot={false} />
            </LineChart>
          ) : (
            <AreaChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id={`grad-${tid}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={stroke} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={stroke} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_TOKENS.grid} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: CHART_TOKENS.axisText }} />
              <YAxis tick={{ fontSize: 10, fill: CHART_TOKENS.axisText }} allowDecimals={false} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Area type="monotone" dataKey="value" stroke={stroke} strokeWidth={2} fill={`url(#grad-${tid})`} />
            </AreaChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const currency = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const percent = (v) => `${Math.round((v || 0) * 1000) / 10}%`;

const ActivityIcon = ({ kind }) => {
  const Icon = kind === 'signup' ? Users : kind === 'ticket' ? LifeBuoy : kind === 'scan' ? Search : Download;
  return <Icon size={12} />;
};

export const AdminLive = () => {
  const [range, setRange] = useState('30d');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [inbound, setInbound] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/admin/business-health?range=${range}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || 'Failed to load business health');
      }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [range]);

  useEffect(() => {
    // One-time — inbound-email feature-flag state so the ops widget can tell
    // the Super Admin what (if anything) is pending owner-side config.
    api.get('/support/inbound/status').then((r) => setInbound(r.data)).catch(() => {});
  }, []);

  const kpis = data?.kpis || {};
  const trends = data?.trends || {};
  const activity = data?.activity || [];
  const connectors = data?.connectors || [];

  const trialConversionLabel = useMemo(() => percent(kpis.trial_conversion_rate || 0), [kpis]);

  if (err) return <div className="text-sm text-zita-mocha" data-testid="admin-live-error">{err}</div>;
  if (!data) return <div className="text-sm text-zita-mocha" data-testid="admin-live-loading">Loading Business Health…</div>;

  return (
    <div data-testid="admin-business-health">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif text-3xl text-zita-espresso">Business Health</h1>
          <p className="text-xs text-zita-mocha mt-1">
            Real-time snapshot of Zita. {data.note}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-wider text-zita-mocha">Range</label>
          <select
            data-testid="admin-bh-range"
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="zita-input px-3 py-2 text-sm"
          >
            {RANGES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
          </select>
        </div>
      </div>

      {/* KPI CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard icon={Users} label="Total users" value={kpis.total_users} sub={`${kpis.admins} admin · ${kpis.free_users} free · ${kpis.paid_users} paid`} tid="kpi-total-users" />
        <KpiCard icon={Activity} label="Active (30d)" value={kpis.active_30d} sub="unique sessions" tid="kpi-active-30d" />
        <KpiCard icon={CreditCard} label="Active subs" value={kpis.active} sub={`Trialing ${kpis.trialing} · Past due ${kpis.past_due}`} tid="kpi-active-subs" />
        <KpiCard icon={DollarSign} label="Est. MRR" value={currency(kpis.estimated_mrr_usd)} sub={`ARR ${currency(kpis.estimated_arr_usd)}`} tid="kpi-mrr" accent="bg-emerald-100 text-emerald-800" />
        <KpiCard icon={TrendingUp} label="Trial → Paid" value={trialConversionLabel} sub="lifetime conversion" tid="kpi-conversion" />
        <KpiCard icon={XCircle} label="Cancelled" value={kpis.canceled} sub="total lifetime" tid="kpi-canceled" />
        <KpiCard icon={Zap} label="Legacy addon (grandfathered)" value={kpis.addon_active} sub="Retired — kept for historical accounting" tid="kpi-addon" accent="bg-ari/10 ari-accent" />
        <KpiCard icon={LifeBuoy} label="Open tickets" value={kpis.open_tickets} sub={`${kpis.critical_tickets} critical · ${kpis.human_takeovers} human takeovers`} tid="kpi-open-tickets" />
        <KpiCard icon={Search} label="Total scans" value={kpis.total_scans} sub={`${kpis.running_scans} running · ${kpis.failed_scans} failed`} tid="kpi-scans" />
        <KpiCard icon={Download} label="Total exports" value={kpis.total_exports} sub="CSV / XLSX / PDF" tid="kpi-exports" />
        <KpiCard icon={Bot} label="Ari chats" value={kpis.total_ari_chats} sub="all-time messages" tid="kpi-ari" accent="bg-ari/10 ari-accent" />
        <KpiCard icon={Server} label="Database" value={kpis.db_ok ? 'OK' : 'DOWN'} sub={kpis.db_ok ? 'reachable' : 'attention required'} tid="kpi-db" accent={kpis.db_ok ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'} />
      </div>

      {/* INBOUND EMAIL STATUS — inbound is the ONLY email feature pending. Outbound is separate and works. */}
      {inbound && !inbound.inbound_ready && (
        <div className="mb-6 zita-card p-4 border border-amber-300" data-testid="admin-inbound-status">
          <div className="flex items-center gap-2 text-sm text-amber-900">
            <ShieldAlert size={16} className="shrink-0" />
            <div>
              <div className="font-semibold">
                {inbound.outbound_email_configured
                  ? 'Customer email replies — pending SendGrid Inbound Parse setup'
                  : 'Email delivery — SendGrid API key not set'}
              </div>
              <div className="text-xs mt-1 text-amber-800">
                {inbound.outbound_email_configured ? (
                  <>
                    Outbound email (welcome, receipts, ticket confirmations, admin replies)
                    is delivering normally. What&apos;s still pending is the <strong>inbound</strong>
                    {' '}channel — customers cannot reply <em>by email</em> to a ticket yet.
                    To enable: set <code>ZITA_INBOUND_SECRET</code> and{' '}
                    <code>SENDGRID_INBOUND_HOSTNAME</code> in the backend <code>.env</code>, add the DNS
                    MX record, and point SendGrid Inbound Parse for that hostname at{' '}
                    <code>&lt;PUBLIC_API_URL&gt;/api/support/inbound</code> with header{' '}
                    <code>X-Zita-Inbound-Secret</code>.
                  </>
                ) : (
                  <>Set <code>SENDGRID_API_KEY</code> in the backend <code>.env</code> to enable all Zita emails
                  (welcome, receipts, password reset, ticket confirmations, admin replies).</>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TREND CHARTS */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <TrendCard title="New signups" series={trends.signups} tid="trend-signups" stroke={CHART_TOKENS.signups} />
        <TrendCard title="Revenue (paid transactions)" series={trends.revenue_usd} tid="trend-revenue" kind="bar" stroke={CHART_TOKENS.revenue} valueFmt={currency} />
        <TrendCard title="Cancellations" series={trends.cancellations} tid="trend-cancels" kind="line" stroke={CHART_TOKENS.cancellations} />
        <TrendCard title="Failed payments" series={trends.failed_payments} tid="trend-failed" kind="line" stroke={CHART_TOKENS.failedPayments} />
        <TrendCard title="Search activity" series={trends.scans} tid="trend-scans" kind="area" stroke={CHART_TOKENS.scans} />
        <TrendCard title="Legacy addon usage" series={trends.exports} tid="trend-exports" kind="area" stroke={CHART_TOKENS.exports} />
        <TrendCard title="Ari chat volume" series={trends.ari_chats} tid="trend-ari" kind="area" stroke={CHART_TOKENS.ariChats} />
        <TrendCard title="Support tickets opened" series={trends.tickets} tid="trend-tickets" kind="bar" stroke={CHART_TOKENS.tickets} />
        <TrendCard title="Human takeovers" series={trends.human_takeovers} tid="trend-takeovers" kind="bar" stroke={CHART_TOKENS.humanTakeover} />
      </div>

      {/* ACTIVITY FEED + CONNECTORS */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="zita-card p-5" data-testid="admin-activity-feed">
          <div className="text-xs uppercase tracking-wider text-zita-mocha mb-3">Recent activity</div>
          <div className="space-y-3">
            {!activity.length && <div className="text-sm text-zita-mocha">No recent activity.</div>}
            {activity.map((a, i) => (
              <div key={i} className="flex items-start gap-3 text-sm">
                <div className="w-6 h-6 rounded-full bg-zita-sand text-zita-cocoa flex items-center justify-center mt-0.5">
                  <ActivityIcon kind={a.kind} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-zita-espresso truncate">{a.text}</div>
                  <div className="text-[11px] text-zita-mocha">{a.at ? new Date(a.at).toLocaleString() : ''}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="zita-card p-5" data-testid="admin-connector-health">
          <div className="text-xs uppercase tracking-wider text-zita-mocha mb-3">Connector & system health</div>
          {!connectors.length && (
            <div className="text-sm text-zita-mocha">No connector activity yet. Connect a source to see health metrics.</div>
          )}
          {!!connectors.length && (
            <table className="w-full text-sm">
              <thead className="text-zita-mocha uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="text-left pb-2">Source</th>
                  <th className="text-left pb-2">Users</th>
                  <th className="text-left pb-2">Last success</th>
                  <th className="text-left pb-2">Errors</th>
                </tr>
              </thead>
              <tbody>
                {connectors.map((c) => (
                  <tr key={c.source_key} className="border-t border-zita-taupe/40">
                    <td className="py-2 font-medium text-zita-espresso">{c.source_key}</td>
                    <td className="py-2 text-zita-mocha">{c.connected_users}</td>
                    <td className="py-2 text-xs text-zita-mocha">{c.last_success_at ? new Date(c.last_success_at).toLocaleString() : '—'}</td>
                    <td className="py-2 text-zita-mocha inline-flex items-center gap-1">
                      {c.errors > 0 && <AlertTriangle size={12} className="text-amber-700" />}
                      {c.errors}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="text-[11px] text-zita-muted mt-6" data-testid="admin-bh-timestamp">
        Snapshot generated at {new Date(data.generated_at).toLocaleTimeString()} · refreshes every 30s
      </div>
    </div>
  );
};

export default AdminLive;
